/**
 * Sub-Ledger Reconciliation Manager Component
 * 
 * Reconciles AR/AP detail accounts to GL control accounts with aging analysis
 */

import React, { useState, useEffect } from 'react';
import { subLedgerReconciliationAPI, chartOfAccountsAPI } from '../services/accountingService';
import {
  ClipboardText, PlusCircle, MagnifyingGlass, ArrowsClockwise, ChartBar,
  CheckCircle, CalendarBlank, Lightbulb, Sparkle,
} from '@phosphor-icons/react';
import '../styles/subLedgerReconciliationManager.css';

const SubLedgerReconciliationManager = () => {
  const [reconciliations, setReconciliations] = useState([]);
  const [activeReconciliation, setActiveReconciliation] = useState(null);
  const [agingAnalysis, setAgingAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [glAccounts, setGlAccounts] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    subledger_type: 'AR',
    control_account_id: '',
    reconciliation_date: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const accounts = await chartOfAccountsAPI.getAccountsByType('AR');
      setGlAccounts(accounts || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReconciliation = async (e) => {
    e.preventDefault();
    try {
      const result = await subLedgerReconciliationAPI.createReconciliation(formData);
      setActiveReconciliation(result);
      setFormData({
        subledger_type: 'AR',
        control_account_id: '',
        reconciliation_date: '',
      });
      setShowCreateForm(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAutoMatch = async (reconciliationId) => {
    try {
      setLoading(true);
      await subLedgerReconciliationAPI.autoMatchDetails(reconciliationId);
      await loadReconciliationDetails(reconciliationId);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGetAging = async (reconciliationId) => {
    try {
      const analysis = await subLedgerReconciliationAPI.getAgingAnalysis(reconciliationId);
      setAgingAnalysis(analysis);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleComplete = async (reconciliationId) => {
    try {
      await subLedgerReconciliationAPI.completeReconciliation(reconciliationId);
      await loadReconciliationDetails(reconciliationId);
    } catch (err) {
      setError(err.message);
    }
  };

  const loadReconciliationDetails = async (reconciliationId) => {
    try {
      const summary = await subLedgerReconciliationAPI.getSummary(reconciliationId);
      setActiveReconciliation(summary);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="loading">Loading sub-ledger reconciliation...</div>;

  return (
    <div className="subledger-reconciliation-manager">
      <div className="header">
        <h2><ClipboardText size={20} /> Sub-Ledger Reconciliation</h2>
        <p>Reconcile AR/AP detail accounts to GL control accounts</p>
        <button 
          className="btn-primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          <PlusCircle size={16} /> New Reconciliation
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showCreateForm && (
        <form onSubmit={handleCreateReconciliation} className="create-recon-form">
          <h3>Create Sub-Ledger Reconciliation</h3>
          <div className="form-group">
            <label>Sub-Ledger Type</label>
            <select
              value={formData.subledger_type}
              onChange={(e) => setFormData({...formData, subledger_type: e.target.value})}
            >
              <option value="AR">Accounts Receivable</option>
              <option value="AP">Accounts Payable</option>
              <option value="HOSTEL_DEPOSITS">Hostel Deposits</option>
              <option value="EMPLOYEE_ADVANCES">Employee Advances</option>
            </select>
          </div>
          <div className="form-group">
            <label>Control Account</label>
            <select
              value={formData.control_account_id}
              onChange={(e) => setFormData({...formData, control_account_id: e.target.value})}
              required
            >
              <option value="">Select Account</option>
              {glAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.account_number} - {account.account_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Reconciliation Date</label>
            <input
              type="date"
              value={formData.reconciliation_date}
              onChange={(e) => setFormData({...formData, reconciliation_date: e.target.value})}
              required
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-primary">Create</button>
            <button 
              type="button" 
              className="btn-secondary"
              onClick={() => setShowCreateForm(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {activeReconciliation && (
        <div className="reconciliation-detail">
          <h3><MagnifyingGlass size={18} /> Reconciliation Details</h3>
          <div className="stat-grid">
            <div className="stat-card">
              <h4>GL Balance</h4>
              <p className="value">${activeReconciliation.gl_balance?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="stat-card">
              <h4>Detail Balance</h4>
              <p className="value">${activeReconciliation.detail_balance?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="stat-card">
              <h4>Matched Items</h4>
              <p className="value">{activeReconciliation.matched_count || 0}</p>
            </div>
            <div className="stat-card">
              <h4>Unmatched Items</h4>
              <p className="value">{activeReconciliation.unmatched_count || 0}</p>
            </div>
            <div className="stat-card">
              <h4>Variance</h4>
              <p className={`value ${activeReconciliation.variance === 0 ? 'balanced' : 'unbalanced'}`}>
                ${activeReconciliation.variance?.toFixed(2) || '0.00'}
              </p>
            </div>
            <div className="stat-card">
              <h4>Status</h4>
              <p className="value">
                <span className={`badge badge-${activeReconciliation.status}`}>
                  {activeReconciliation.status}
                </span>
              </p>
            </div>
          </div>

          <div className="reconciliation-actions">
            <button 
              className="btn-primary"
              onClick={() => handleAutoMatch(activeReconciliation.id)}
            >
              <ArrowsClockwise size={16} /> Auto-Match Details
            </button>
            <button 
              className="btn-secondary"
              onClick={() => handleGetAging(activeReconciliation.id)}
            >
              <ChartBar size={16} /> Aging Analysis
            </button>
            {activeReconciliation.variance === 0 && activeReconciliation.status !== 'COMPLETED' && (
              <button 
                className="btn-success"
                onClick={() => handleComplete(activeReconciliation.id)}
              >
                <CheckCircle size={16} /> Complete Reconciliation
              </button>
            )}
          </div>
        </div>
      )}

      {agingAnalysis && (
        <div className="aging-analysis">
          <h3><CalendarBlank size={18} /> Aging Analysis</h3>
          <div className="aging-buckets">
            <div className="bucket">
              <h4>Current (0-30 days)</h4>
              <div className="bucket-content">
                <p className="amount">${agingAnalysis.current_0_30?.toFixed(2) || '0.00'}</p>
                <p className="percentage">{((agingAnalysis.current_0_30 / agingAnalysis.total) * 100).toFixed(1)}%</p>
                <div className="bucket-bar" style={{ width: `${((agingAnalysis.current_0_30 / agingAnalysis.total) * 100)}%` }}></div>
              </div>
            </div>
            <div className="bucket">
              <h4>30-60 Days</h4>
              <div className="bucket-content">
                <p className="amount">${agingAnalysis.days_30_60?.toFixed(2) || '0.00'}</p>
                <p className="percentage">{((agingAnalysis.days_30_60 / agingAnalysis.total) * 100).toFixed(1)}%</p>
                <div className="bucket-bar warning" style={{ width: `${((agingAnalysis.days_30_60 / agingAnalysis.total) * 100)}%` }}></div>
              </div>
            </div>
            <div className="bucket">
              <h4>60-90 Days</h4>
              <div className="bucket-content">
                <p className="amount">${agingAnalysis.days_60_90?.toFixed(2) || '0.00'}</p>
                <p className="percentage">{((agingAnalysis.days_60_90 / agingAnalysis.total) * 100).toFixed(1)}%</p>
                <div className="bucket-bar danger" style={{ width: `${((agingAnalysis.days_60_90 / agingAnalysis.total) * 100)}%` }}></div>
              </div>
            </div>
            <div className="bucket">
              <h4>90+ Days</h4>
              <div className="bucket-content">
                <p className="amount">${agingAnalysis.days_90_plus?.toFixed(2) || '0.00'}</p>
                <p className="percentage">{((agingAnalysis.days_90_plus / agingAnalysis.total) * 100).toFixed(1)}%</p>
                <div className="bucket-bar critical" style={{ width: `${((agingAnalysis.days_90_plus / agingAnalysis.total) * 100)}%` }}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="subledger-info">
        <h4><Lightbulb size={18} /> Sub-Ledger Reconciliation Process</h4>
        <ol>
          <li><strong>Create Reconciliation:</strong> Select subledger type and control account</li>
          <li><strong>Auto-Match:</strong> System matches detail records to GL entries</li>
          <li><strong>Review Unmatched:</strong> Identify and resolve exceptions</li>
          <li><strong>Aging Analysis:</strong> View aging buckets (0-30, 30-60, 60-90, 90+)</li>
          <li><strong>Complete:</strong> Reconciliation complete when variances resolved</li>
        </ol>
        <h4><Sparkle size={18} /> Features</h4>
        <ul>
          <li>Automatic matching with $1.00 tolerance</li>
          <li>Aging analysis with 4 buckets (current to 90+ days)</li>
          <li>Unmatched detail tracking</li>
          <li>Variance reporting</li>
          <li>AR/AP/Deposit/Advance support</li>
        </ul>
      </div>
    </div>
  );
};

export default SubLedgerReconciliationManager;
